import { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { DoorState, LiftState, BuildingMap } from '../../api/ws-types';
import type { components } from '../../api/generated/openapi';
type AffineImage = components['schemas']['AffineImage'];
import { useTrajectories } from '../../api/hooks/useTrajectories';
import { useRobots } from '../../api/hooks';
import { useDoors } from '../../api/hooks/useDoors';
import { useLifts } from '../../api/hooks/useLifts';
import { usePlaces } from '../../api/hooks/usePlaces';
import type { LiftLook } from '../Devices/lift-column-state';
import { useLiftCarViews } from '../Devices/useLiftColumnState';
import type { PlaceRegistry } from '../../api/v1';
import { useFeedHealth } from '../../api/feed-health';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { AuthContext } from '../../auth/AuthContext';
import { audienceFor } from '../../auth/roles';
import { DOCK_LABEL, dockWords, docksOnLevel } from './dock-places';
import { useDockPlaces } from './useDockPlaces';
import { sameLevel } from '../../utils/levels';
import { hitTest } from './map-hit-test';
import { NO_ANCHOR, type MapAnchor, type MapBubbleRendererProps, type MapDevice } from './map-bubble-anchor';
import type { RobotPresence } from './robot-presence';
import { robotMarksForLevel, trajectoriesForAudience, type RobotMark } from './robot-marks';
import { useRobotPresence } from './useRobotPresence';
import { DOOR_FLASH_PERIOD_MS, worldToScreen } from './map-renderer';
import { useActiveSite } from '../../state/useActiveSite';
import { useUserPreferences } from '../../state/useUserPreferences';
import { CleanedLegend } from '../Twin/CleanedLegend';
import type { MapCleanedLayer } from './MapCleanedZones';
import { emptyBBox, expandBBox } from './map-utils';
import {
  ALL_LAYERS_ON,
  computeFitToBuilding,
  floorplanWorldCorners,
  LAYER_LABELS,
  mapHighlightPlan,
  panCamera,
  renderMap,
  VISIBLE_LAYER_KEYS,
  type Camera,
  type DockMark,
  type FloorplanImage,
  type LayerToggles,
  type MapHighlight,
} from './map-renderer';
import './BuildingMapCanvas.css';

// Manual zoom (wheel + +/- buttons) used to clamp to these fixed
// constants regardless of building size. A real building's "Fit"
// scale (px per world-metre) is a function of its physical span vs.
// the canvas size and routinely exceeded MAX_SCALE=20 (e.g. a
// ~15x9m room in an 800x480 canvas fits at ~scale 44) — so Fit could
// reach a zoom level manual controls could never reach, and even
// snapped back down to MAX_SCALE the moment + was pressed afterward.
// Zoom bounds are now derived from the last computed fit scale
// instead (see `fitScaleRef` below) so manual zoom always brackets
// whatever Fit just computed for the current building/level.
const ZOOM_RANGE_FACTOR = 50;
const ABSOLUTE_MIN_SCALE = 0.001;
const ABSOLUTE_MAX_SCALE = 5000;

/** The Robots layer unticked: one stable empty list for all three users. */
const NO_MARKS: readonly RobotMark[] = Object.freeze([]);
/** No dock drawn (none on this floor, or the Places layer unticked). */
const NO_DOCKS: readonly DockMark[] = Object.freeze([]);

/** The cleaned-zone colouring's worker (`MapCleanedZones`): lazy, so the
 *  building model and the cell painter load only for a person who has
 *  switched the colouring on for this map (Preferences; off by default). */
const MapCleanedZones = lazy(() => import('./MapCleanedZones'));

export function BuildingMapCanvas({
  buildingMap,
  onSelectRobot,
  onSelectDoor,
  onSelectLift,
  onSelectPlace,
  anchorSourceRef,
  onTapNothing,
  onHoverDevice,
  robotPresence,
  liftLooks,
  highlight = null,
}: MapBubbleRendererProps & {
  buildingMap: BuildingMap | undefined;
  onSelectRobot?: (robotName: string) => void;
  onSelectDoor?: (doorName: string) => void;
  onSelectLift?: (liftName: string) => void;
  /** Fires for a click on a CLEANING-ZONE marker only — the ordinary
   *  waypoint dots are texture, not targets (there are hundreds of
   *  them per building and nothing to do with one). The parent decides
   *  what a zone click means; `OverviewScreen` opens a prefilled Clean
   *  dispatch, and omitting the prop leaves the markers inert. */
  onSelectPlace?: (place: PlaceRegistry) => void;
  /**
   * Every robot's presence (`useRobotPresence`), when a host that shares
   * one reading between several surfaces already holds it. Absent, the
   * canvas reads its own from the same robots and lifts -- the same
   * answer, because presence is one classification of the same data.
   */
  robotPresence?: ReadonlyMap<string, RobotPresence>;
  /**
   * What each lift car may be drawn as, by name: the host's
   * `useLiftColumnState` result, so the map and the attention list read
   * one state. Without it the canvas reads the same receipts itself
   * (`useLiftCarViews`), so a car is never drawn from its raw row.
   */
  liftLooks?: ReadonlyMap<string, LiftLook>;
  /**
   * The ONE highlight prop (contract C0 §7, W4 step 12): what the map is
   * asked to point at, `{kind, id}` as the locate contract spells a focus
   * (a robot by `<fleet>/<robot>`). It is ringed where this floor's map
   * draws it (`mapHighlightPlan`), and nowhere else. A ring never moves
   * the camera and takes no tap.
   */
  highlight?: MapHighlight | null;
}) {
  const { site } = useActiveSite();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 800, h: 480 });
  const [activeLevelName, setActiveLevelName] = useState<string | null>(null);
  const [layers, setLayers] = useState<LayerToggles>(ALL_LAYERS_ON);
  const [camera, setCamera] = useState<Camera>({ offsetX: 0, offsetY: 0, scale: 4, width: 800, height: 480 });
  // A mirror of `camera`, for the pointer handlers.
  //
  // A gesture can outlive several renders and the handlers that start
  // one read the camera as it is AT THAT MOMENT -- when a pinch ends
  // and a pan resumes from the remaining finger, the base offsets must
  // be the ones the pinch just produced, not the ones captured when
  // the component last rendered. The same reason `fitScaleRef` exists
  // a few lines below.
  const cameraRef = useRef(camera);
  cameraRef.current = camera;
  // Last "Fit" scale, read fresh (not a stale closure) by the wheel
  // handler and +/- buttons to derive manual-zoom bounds — see the
  // `ZOOM_RANGE_FACTOR` comment above.
  const fitScaleRef = useRef(4);
  const scaleBounds = useCallback(() => {
    const base = fitScaleRef.current;
    return {
      min: Math.max(ABSOLUTE_MIN_SCALE, base / ZOOM_RANGE_FACTOR),
      max: Math.min(ABSOLUTE_MAX_SCALE, base * ZOOM_RANGE_FACTOR),
    };
  }, []);

  // Default to the first level when the building map first arrives.
  useEffect(() => {
    if (!buildingMap) return;
    if (activeLevelName && buildingMap.levels.some((l) => l.name === activeLevelName)) return;
    const first = buildingMap.levels[0]?.name ?? null;
    setActiveLevelName(first);
  }, [buildingMap, activeLevelName]);

  // Live data subscriptions. Trajectories are published per-level (e.g.
  // "L1"), not per-building (`buildingMap.name` is the whole site, e.g.
  // "hotel") — must key off `activeLevelName`, not `buildingMap.name`,
  // or this always misses the cache (confirmed live: the query cache
  // was correctly populated under the level key, but this component
  // was reading it back under the wrong key and always getting null).
  // `site` is nullable on the context but not here: the only caller,
  // `OverviewScreen`, returns `NoSiteNotice` before rendering this. An
  // empty id disables the query, so a future caller that forgets the gate
  // gets no trajectories rather than a crash.
  const trajectories = useTrajectories(site?.id ?? '', activeLevelName);
  const robotsQuery = useRobots();
  const doorsQuery = useDoors();
  const liftsQuery = useLifts();
  // Named waypoints. NOT part of the building-map payload: the v1
  // `BuildingMapSnapshot` carries no place list, so the adapter that
  // builds the legacy `BuildingMap` shape hardcodes `places: []` and
  // the layer drew nothing however it was toggled. They have their own
  // endpoint (`GET /api/v1/sites/{site}/places`) and their own hook,
  // which until now only `TaskDispatchForm` consumed.
  const placesQuery = usePlaces();
  // The site's charging docks: the task catalog's `charge` places, at
  // their registry rows (`dock-places.ts`). Both are cached queries the
  // dispatch form reads too; nothing new is fetched. Worded for the
  // reader: the auth CONTEXT, as `useCanSeeSimulated` reads it -- no
  // provider is no roles, a site audience.
  const allDocks = useDockPlaces();
  const audience = audienceFor(useContext(AuthContext)?.roles);

  // ---- Robot marks: what may honestly be drawn (`robot-marks.ts`) ----
  //
  // Presence decides solid / dim / ghost / nothing; feed health clamps
  // the whole canvas to last-known; a site audience never sees the
  // simulated fleet. The resulting list is handed to the renderer, the
  // hit test AND `data-robot-marks`, so a robot is clickable, and listed,
  // exactly where it is drawn -- which is nowhere while the Robots layer
  // is unticked.
  const ownPresence = useRobotPresence(robotsQuery.data, liftsQuery.data);
  const presence = robotPresence ?? ownPresence;
  const feed = useFeedHealth(site?.id ?? '');
  const showSimulated = useCanSeeSimulated();
  const robotMarks = useMemo(
    () =>
      layers.robots
        ? robotMarksForLevel(
            robotsQuery.data,
            { presence, clamped: feed.clamped, showSimulated },
            activeLevelName ?? '',
          )
        : NO_MARKS,
    [layers.robots, robotsQuery.data, presence, feed.clamped, showSimulated, activeLevelName],
  );
  // The simulated fleet's planned paths go with its dots.
  const shownTrajectories = useMemo(
    () => trajectoriesForAudience(trajectories ?? undefined, showSimulated),
    [trajectories, showSimulated],
  );

  // Track viewport size via ResizeObserver. We observe the
  // *viewport div* (not the canvas itself) because the canvas is
  // sized via CSS (`width: 100%; height: 100%`) and fills the
  // viewport — observing the canvas would never fire when the
  // viewport grows because the canvas's CSS size is fixed at 100%.
  // The canvas's intrinsic `width` / `height` attributes are then
  // scaled by DPR for HiDPI rendering.
  useEffect(() => {
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !viewport) return;

    const syncSize = () => {
      const rect = viewport.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(100, Math.floor(rect.width));
      const h = Math.max(100, Math.floor(rect.height));
      if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        setSize({ w, h });
        setCamera((c) => ({ ...c, width: w, height: h }));
      }
    };

    // Synchronously size the backing buffer to the initial CSS size
    // so the first render is at full resolution (default is 300×150,
    // which causes a stretched first paint until the observer fires).
    syncSize();

    const ro = new ResizeObserver(() => syncSize());
    ro.observe(viewport);
    return () => ro.disconnect();
  }, []);

  // Decode the floorplan image (AffineImage.data is base64).
  const [floorplan, setFloorplan] = useState<FloorplanImage | null>(null);
  useEffect(() => {
    if (!buildingMap || !activeLevelName) {
      setFloorplan(null);
      return;
    }
    const level = buildingMap.levels.find((l) => l.name === activeLevelName);
    const img: AffineImage | undefined = level?.images?.[0];
    if (!img || !img.data) {
      setFloorplan(null);
      return;
    }
    const src = typeof img.data === 'string' && /^https?:\/\//.test(img.data)
      ? img.data
      : `data:image/${img.encoding || 'png'};base64,${img.data}`;
    const html = new Image();
    let cancelled = false;
    html.onload = () => {
      if (cancelled) return;
      setFloorplan({
        html,
        pixelWidth: html.naturalWidth,
        pixelHeight: html.naturalHeight,
        affine: { x_offset: img.x_offset, y_offset: img.y_offset, yaw: img.yaw, scale: img.scale },
      });
    };
    html.onerror = () => {
      if (cancelled) return;
      setFloorplan({
        html: null,
        pixelWidth: 0,
        pixelHeight: 0,
        affine: { x_offset: img.x_offset, y_offset: img.y_offset, yaw: img.yaw, scale: img.scale },
      });
    };
    html.src = src;
    return () => {
      cancelled = true;
    };
  }, [buildingMap, activeLevelName]);

  // Render on every state change (dirty flag is implicit — React
  // re-renders on hook updates; the canvas redraw is cheap).
  const activeLevel = useMemo(
    () => buildingMap?.levels.find((l) => l.name === activeLevelName),
    [buildingMap, activeLevelName],
  );
  const liveDoorStates = useMemo(() => {
    const out: Record<string, DoorState | undefined> = {};
    for (const d of doorsQuery.data ?? []) out[d.name] = d as unknown as DoorState;
    return out;
  }, [doorsQuery.data]);
  const liveLiftStates = useMemo(() => {
    const out: Record<string, LiftState | undefined> = {};
    for (const l of liftsQuery.data ?? []) out[l.name] = l as unknown as LiftState;
    return out;
  }, [liftsQuery.data]);
  // Lift looks (W6 step 13): the host's, or this canvas's own reading of
  // the same per-page receipts. Always computed, as a hook must be.
  const mapLiftNames = useMemo(() => (buildingMap?.lifts ?? []).map((l) => l.name), [buildingMap]);
  const ownLiftLooks = useLiftCarViews(mapLiftNames, liftsQuery.data);
  const looks: ReadonlyMap<string, LiftLook> = liftLooks ?? ownLiftLooks;
  // Places for the floor on screen. `PlaceRegistry.level` uses the same
  // vocabulary as `buildingMap.levels[].name` (the worker fills it by
  // name lookup against the building map), so equality is the filter —
  // except that the lookup can fail: a registry published BEFORE the
  // building map arrived carries `level: ''`. Those are kept rather
  // than dropped, because "unknown floor" is a transient worker
  // ordering artefact, and hiding the whole layer until a republish is
  // worse than briefly showing a place on the wrong floor.
  const levelPlaces = useMemo<PlaceRegistry[]>(() => {
    const name = activeLevelName ?? '';
    return (placesQuery.data ?? []).filter((p) => p.level === name || p.level === '');
  }, [placesQuery.data, activeLevelName]);

  // The docks on the floor on screen, drawn with the Places layer. Strict
  // floor match (`docksOnLevel`): unlike a waypoint dot, a dock whose floor
  // the registry could not name is not drawn anywhere.
  const levelDocks = useMemo(
    () => (layers.places ? docksOnLevel(allDocks, activeLevelName) : []),
    [layers.places, allDocks, activeLevelName],
  );
  // The cleaned-zone colouring, as the person set it in Preferences. The
  // worker paints off-canvas and hands its layer up; switched off, it is
  // unmounted and nothing of it is drawn, even a layer it left behind.
  const { prefs } = useUserPreferences();
  const colourZones = prefs.cleanedZones.map;
  const [cleanedLayer, setCleanedLayer] = useState<MapCleanedLayer | null>(null);
  const zoneLayer = colourZones ? cleanedLayer : null;

  const dockMarks = useMemo<readonly DockMark[]>(
    () =>
      levelDocks.length === 0
        ? NO_DOCKS
        : levelDocks.map((d) => ({ x: d.x, y: d.y, label: dockWords(d, audience) })),
    [levelDocks, audience],
  );

  // The ring, from exactly what this floor draws: the same marks, places
  // and car looks the layers below are drawn from.
  const highlightPlan = useMemo(
    () =>
      mapHighlightPlan({
        highlight,
        robotMarks,
        buildingMap,
        activeLevel,
        activeLevelName: activeLevelName ?? '',
        places: levelPlaces,
        liftLooks: looks,
      }),
    [highlight, robotMarks, buildingMap, activeLevel, activeLevelName, levelPlaces, looks],
  );

  // A moving door/lift-door, or a robot with an upcoming (not yet
  // traversed) path segment, is transient and worth catching at a
  // glance on the map — a static outline didn't read as "actively
  // happening / about to happen" from a distance. Flash only runs
  // (the interval only exists) while something is actually moving or
  // about to move, so an idle building doesn't pay for a redraw timer
  // it doesn't need.
  const hasUpcomingTrajectory = useMemo(
    () =>
      (shownTrajectories?.trajectories ?? []).some((t) => {
        const segs = t.segments ?? [];
        const nowMs = shownTrajectories?.now_ms;
        return segs.length > 0 && (nowMs === undefined || segs[segs.length - 1]!.t > nowMs);
      }),
    [shownTrajectories],
  );
  const hasMovingDoor = useMemo(
    () =>
      (doorsQuery.data ?? []).some((d) => d.state === 'moving') ||
      // Only a live car carries its door state; a stale "moving" must not
      // keep the map flashing.
      [...looks.values()].some((l) => l.door === 'moving') ||
      hasUpcomingTrajectory,
    [doorsQuery.data, looks, hasUpcomingTrajectory],
  );
  const [flashOn, setFlashOn] = useState(false);
  useEffect(() => {
    if (!hasMovingDoor) {
      setFlashOn(false);
      return;
    }
    const id = window.setInterval(() => setFlashOn((v) => !v), DOOR_FLASH_PERIOD_MS);
    return () => window.clearInterval(id);
  }, [hasMovingDoor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderMap({
      ctx,
      cam: camera,
      buildingMap,
      activeLevel,
      activeLevelName: activeLevelName ?? '',
      floorplan,
      robotMarks,
      trajectories: shownTrajectories,
      liveDoorStates,
      liveLiftStates,
      liftLooks: looks,
      places: levelPlaces,
      docks: dockMarks,
      layers,
      flashOn,
      highlight: highlightPlan,
      zoneCells: zoneLayer?.cells ?? null,
      zoneFills: zoneLayer?.fills ?? null,
    });
  }, [
    camera,
    buildingMap,
    activeLevel,
    activeLevelName,
    floorplan,
    robotMarks,
    shownTrajectories,
    liveDoorStates,
    liveLiftStates,
    looks,
    levelPlaces,
    dockMarks,
    layers,
    size,
    flashOn,
    highlightPlan,
    zoneLayer,
  ]);

  // Auto-fit when the level changes.
  useEffect(() => {
    if (!activeLevel) return;
    // `expandBBox` is PURE (returns a new WorldBBox, does not mutate
    // its argument) — `bb` must be reassigned at every call, hence
    // `let` here, not `const`. Every call site below used to discard
    // the return value as a bare statement, so `bb` stayed at its
    // empty sentinel (`{minX: Infinity, ...}`) the entire time and
    // `!Number.isFinite(bb.minX)` always returned early — the whole
    // fit-to-building calculation has been a silent no-op all along,
    // masked because the untouched default camera state happened to
    // look plausible against the mock's similarly-scaled synthetic
    // data.
    let bb = emptyBBox();
    // Floorplan image bounds FIRST — a real floorplan typically spans
    // a much larger area than the handful of nav-graph/door/lift
    // points it contains, so fitting to those alone zoomed in on a
    // small sub-region and left most of the pane empty. The image
    // loads asynchronously (`floorplan` starts `null`), so this
    // effect must also depend on it below to re-fit once it arrives.
    if (floorplan && floorplan.pixelWidth > 0 && floorplan.pixelHeight > 0) {
      const corners = floorplanWorldCorners(floorplan.affine, floorplan.pixelWidth, floorplan.pixelHeight);
      bb = expandBBox(bb, corners.tl.x, corners.tl.y);
      bb = expandBBox(bb, corners.tr.x, corners.tr.y);
      bb = expandBBox(bb, corners.bl.x, corners.bl.y);
      bb = expandBBox(bb, corners.br.x, corners.br.y);
    }
    for (const n of activeLevel.nav_graphs?.flatMap((g) => g.vertices ?? []) ?? []) {
      bb = expandBBox(bb, n.x, n.y);
    }
    for (const p of activeLevel.places ?? []) bb = expandBBox(bb, p.x, p.y);
    for (const d of activeLevel.doors ?? []) {
      bb = expandBBox(bb, d.v1_x, d.v1_y);
      bb = expandBBox(bb, d.v2_x, d.v2_y);
    }
    for (const lift of buildingMap?.lifts ?? []) {
      if (!lift.levels?.includes(activeLevel.name)) continue;
      const halfW = (lift.width ?? 1) / 2;
      const halfD = (lift.depth ?? 1) / 2;
      bb = expandBBox(bb, lift.ref_x - halfW, lift.ref_y - halfD);
      bb = expandBBox(bb, lift.ref_x + halfW, lift.ref_y + halfD);
    }
    if (!Number.isFinite(bb.minX)) return;
    const fitted = bb;
    setCamera((c) => {
      const next = computeFitToBuilding(fitted, c.width, c.height);
      fitScaleRef.current = next.scale;
      return next;
    });
  }, [activeLevel, buildingMap, floorplan]);

  // ---- Interaction handlers ----
  // A drag is started on ANY left-button pointerdown. We promote it
  // to a pan once the pointer moves more than CLICK_THRESHOLD_PX; if
  // it never crosses the threshold, the pointerup is treated as a
  // click and we run the hit-test for robots/doors/lifts. This is the
  // conventional map-tool behaviour (Google Maps, Mapbox, etc.).
  //
  // EVERY LIVE POINTER, not one. The previous version kept a single
  // `dragRef`, so a second finger's `pointerdown` overwrote the first
  // mid-drag and there was nowhere to hold the state a pinch needs.
  // Combined with `touch-action: none` on the canvas -- which is
  // correct, and which also disables the browser's OWN pinch -- that
  // left the map with no way to zoom by touch at all except the +/-
  // buttons, about nine taps from fit scale to a usable robot target.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef<
    | {
        kind: 'pan';
        startX: number;
        startY: number;
        baseOffsetX: number;
        baseOffsetY: number;
        moved: boolean;
        pointerType: string;
      }
    | {
        kind: 'pinch';
        startDist: number;
        baseScale: number;
        // The world point under the midpoint when the pinch began. Held
        // rather than recomputed so that scaling and panning are one
        // formula: whatever was between the fingers stays between them.
        worldX: number;
        worldY: number;
      }
    | null
  >(null);

  // A FINGER IS NOT A MOUSE. 3px was chosen for a pointing device that
  // lands exactly where it is aimed; a fingertip rolls several pixels
  // during an ordinary tap, so every other tap was classified as a pan
  // and its hit-test silently skipped -- indistinguishable from tapping
  // an empty patch of floor.
  const CLICK_THRESHOLD_MOUSE_PX = 3;
  const CLICK_THRESHOLD_TOUCH_PX = 10;
  // Screen-space floors for the hit test. A fingertip covers roughly
  // 44px and lands within about half that of where the user thinks;
  // a mouse is exact but still needs more than the ~2px a 0.6m robot
  // subtends at fit scale.
  const TAP_TARGET_PX = 22;
  const MOUSE_TARGET_PX = 10;

  function canvasPoint(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function beginPan(clientX: number, clientY: number, pointerType: string) {
    gestureRef.current = {
      kind: 'pan',
      startX: clientX,
      startY: clientY,
      baseOffsetX: cameraRef.current.offsetX,
      baseOffsetY: cameraRef.current.offsetY,
      moved: true, // a gesture resumed after a pinch is never a click
      pointerType,
    };
  }

  function beginPinch() {
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return;
    const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
    const cam = cameraRef.current;
    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;
    gestureRef.current = {
      kind: 'pinch',
      startDist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      baseScale: cam.scale,
      // Inverse of `worldToScreen`'s flipped-Y convention, the same
      // one the wheel handler below uses.
      worldX: midX / cam.scale - cam.offsetX,
      worldY: cam.offsetY - midY / cam.scale,
    };
  }

  // Where a device is on screen now, for the tap bubble: read every frame, so
  // it looks at the latest of everything and only looks.
  const anchorInputsRef = useRef({ robotMarks, activeLevel, buildingMap, liveDoorStates, activeLevelName, presence });
  anchorInputsRef.current = { robotMarks, activeLevel, buildingMap, liveDoorStates, activeLevelName, presence };
  useEffect(() => {
    if (!anchorSourceRef) return;
    const onScreen = (wx: number, wy: number): MapAnchor => {
      const canvas = canvasRef.current;
      if (!canvas) return NO_ANCHOR;
      const rect = canvas.getBoundingClientRect();
      const at = worldToScreen(wx, wy, cameraRef.current);
      return { state: 'visible', x: rect.left + at.x, y: rect.top + at.y };
    };
    anchorSourceRef.current = {
      anchorOf(device: MapDevice): MapAnchor {
        const now = anchorInputsRef.current;
        if (device.kind === 'robot') {
          const mark = now.robotMarks?.find((m) => m.name === device.id);
          if (mark) return onScreen(mark.x, mark.y);
          // Not on this floor. A name two fleets share is not guessed.
          const found = [...(now.presence?.entries() ?? [])].filter(([key]) =>
            key.endsWith(`/${device.id}`),
          );
          const level = found.length === 1 ? found[0]![1].pose?.map : undefined;
          return level && !sameLevel(level, now.activeLevelName ?? '')
            ? { state: 'other-floor', level }
            : NO_ANCHOR;
        }
        if (device.kind === 'lift') {
          const lift = now.buildingMap?.lifts?.find((l) => l.name === device.id);
          if (!lift) return NO_ANCHOR;
          return lift.levels?.includes(now.activeLevelName ?? '') ? onScreen(lift.ref_x, lift.ref_y) : NO_ANCHOR;
        }
        const here = now.activeLevel?.doors?.find((d) => d.name === device.id);
        if (here) {
          const live = now.liveDoorStates[device.id] as
            | { v1_x?: number; v1_y?: number; v2_x?: number; v2_y?: number }
            | undefined;
          return onScreen(
            ((live?.v1_x ?? here.v1_x) + (live?.v2_x ?? here.v2_x)) / 2,
            ((live?.v1_y ?? here.v1_y) + (live?.v2_y ?? here.v2_y)) / 2,
          );
        }
        // The map holds one floor at a time; the door's row knows its own.
        const level = (now.liveDoorStates[device.id] as { level?: string } | undefined)?.level;
        return level && !sameLevel(level, now.activeLevelName ?? '')
          ? { state: 'other-floor', level }
          : NO_ANCHOR;
      },
    };
    return () => {
      anchorSourceRef.current = null;
    };
  }, [anchorSourceRef]);

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    // Left button, middle button, or shift+left all pan. Right click
    // is reserved for the browser context menu and is not captured.
    // Touch reports button 0, so it takes the same path.
    if (e.button !== 0 && e.button !== 1) return;
    pointersRef.current.set(e.pointerId, canvasPoint(e));
    try {
      (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
    } catch {
      // Throws when the pointer is no longer active -- a touch the
      // browser has already cancelled, or a synthetic event. Capture is
      // an optimisation (it keeps moves flowing when the pointer leaves
      // the canvas), not a requirement, so losing it must not lose the
      // gesture. `DayLanes`, which drags a scheduled run along its
      // row, treats capture as optional too.
    }

    if (pointersRef.current.size >= 2) {
      beginPinch();
      return;
    }
    gestureRef.current = {
      kind: 'pan',
      startX: e.clientX,
      startY: e.clientY,
      baseOffsetX: camera.offsetX,
      baseOffsetY: camera.offsetY,
      moved: false,
      pointerType: e.pointerType,
    };
  }

  /** The hit test at a point on the canvas, in canvas pixels. */
  function hitAt(cx: number, cy: number, pointerType: string) {
    // Convert screen → world (inverse of `worldToScreen`'s flipped
    // Y convention: screen_y = (offsetY - world_y) * scale).
    const wx = cx / camera.scale - camera.offsetX;
    const wy = camera.offsetY - cy / camera.scale;
    return hitTest(
      wx,
      wy,
      robotMarks,
      activeLevel,
      buildingMap,
      liveDoorStates,
      liveLiftStates,
      activeLevelName ?? '',
      levelPlaces,
      // A HIT RADIUS THE POINTER CAN ACTUALLY REACH -- see `hitTest`.
      (pointerType === 'touch' ? TAP_TARGET_PX : MOUSE_TARGET_PX) / camera.scale,
    );
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    // The hover peek: a mouse, with nothing pressed. A place has no peek.
    if (onHoverDevice && e.pointerType === 'mouse' && pointersRef.current.size === 0) {
      const { x, y } = canvasPoint(e);
      const hit = hitAt(x, y, e.pointerType);
      onHoverDevice(
        hit && hit.kind !== 'place' ? { kind: hit.kind, id: hit.id } : null,
        e.clientX,
        e.clientY,
      );
    }
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, canvasPoint(e));
    const gesture = gestureRef.current;
    if (!gesture) return;

    if (gesture.kind === 'pinch') {
      const pts = [...pointersRef.current.values()];
      if (pts.length < 2) return;
      const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
      const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      setCamera((c) => {
        const { min, max } = scaleBounds();
        const nextScale = Math.max(
          min,
          Math.min(max, gesture.baseScale * (dist / gesture.startDist)),
        );
        // ONE FORMULA FOR BOTH. Placing the pinch's original world
        // point back under the CURRENT midpoint gives the zoom and the
        // two-finger pan together: spreading the fingers scales, and
        // moving them both translates, without a separate pan branch
        // that could disagree about the flipped Y.
        return {
          ...c,
          scale: nextScale,
          offsetX: midX / nextScale - gesture.worldX,
          offsetY: midY / nextScale + gesture.worldY,
        };
      });
      return;
    }

    const dx = e.clientX - gesture.startX;
    const dy = e.clientY - gesture.startY;
    const threshold =
      gesture.pointerType === 'touch' ? CLICK_THRESHOLD_TOUCH_PX : CLICK_THRESHOLD_MOUSE_PX;
    if (!gesture.moved && Math.hypot(dx, dy) < threshold) return;
    gesture.moved = true;
    // Both axes go through `panCamera`, which owns the convention and
    // is the thing the tests pin. This used to inline `+dx` for X and
    // `-dy` for Y, which are opposite conventions -- see panCamera's
    // comment. Deltas are taken from the drag origin, so pan from the
    // base offsets rather than the current ones.
    const base = panCamera(
      { ...cameraRef.current, offsetX: gesture.baseOffsetX, offsetY: gesture.baseOffsetY },
      dx,
      dy,
    );
    setCamera((c) => ({ ...c, offsetX: base.offsetX, offsetY: base.offsetY }));
  }

  function endPointer(e: React.PointerEvent<HTMLCanvasElement>): 'tap' | null {
    const canvas = e.currentTarget as HTMLCanvasElement;
    if (canvas.hasPointerCapture?.(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    pointersRef.current.delete(e.pointerId);
    const gesture = gestureRef.current;

    // LIFTING ONE FINGER OF A PINCH CONTINUES A PAN with the other,
    // rather than ending the gesture. Without this the map either
    // freezes until both fingers are lifted, or jumps, because the
    // remaining finger's next move would be measured from a start
    // point recorded two fingers ago.
    if (pointersRef.current.size === 1) {
      const [remaining] = [...pointersRef.current.entries()];
      if (remaining) {
        const rect = canvas.getBoundingClientRect();
        beginPan(remaining[1].x + rect.left, remaining[1].y + rect.top, e.pointerType);
      }
      return null;
    }
    if (pointersRef.current.size > 1) return null;

    gestureRef.current = null;
    if (!gesture || gesture.kind !== 'pan') return null;
    return gesture.moved ? null : 'tap';
  }

  function onPointerCancel(e: React.PointerEvent<HTMLCanvasElement>) {
    // A cancel is the browser taking the gesture away -- a system
    // edge-swipe, a phone call, a palm touch. Without this handler the
    // pointer stayed in the map forever, so the NEXT touch was treated
    // as the second finger of a pinch that was never happening.
    endPointer(e);
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (endPointer(e) !== 'tap') return;
    // Tap → hit test.
    const { x: cx, y: cy } = canvasPoint(e);
    const hit = hitAt(cx, cy, e.pointerType);
    if (!hit) {
      onTapNothing?.();
      return;
    }
    if (hit.kind === 'robot' && onSelectRobot) onSelectRobot(hit.id);
    else if (hit.kind === 'door' && onSelectDoor) onSelectDoor(hit.id);
    else if (hit.kind === 'lift' && onSelectLift) onSelectLift(hit.id);
    else if (hit.kind === 'place' && onSelectPlace) {
      // Hand back the whole registry entry, not just the name: the
      // parent needs `fleets` and `is_cleaning_zone` to decide what to
      // offer, and re-looking-it-up would mean duplicating the level
      // filter on the other side of the prop.
      const place = levelPlaces.find((p) => p.name === hit.id);
      if (place) onSelectPlace(place);
    }
  }
  // Attached natively with `{ passive: false }` rather than via JSX
  // `onWheel` — React (17+) registers its synthetic wheel listener as
  // passive for scroll-performance reasons, which silently drops
  // `preventDefault()` (Chrome logs "Unable to preventDefault inside
  // passive event listener invocation") and lets the page scroll
  // instead of / alongside the intended zoom.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setCamera((c) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const { min, max } = scaleBounds();
        const nextScale = Math.max(min, Math.min(max, c.scale * factor));
        // Keep the world point under the cursor stationary. Y uses
        // `worldToScreen`'s flipped convention (screen_y = (offsetY -
        // world_y) * scale): inverse is world_y = offsetY -
        // screen_y/scale, and re-solving for the offset that keeps
        // the same world point under the cursor at the new scale is
        // offsetY_new = screen_y/nextScale + world_y (sign flipped
        // vs. the X analogue, which has no such flip).
        const wx = cx / c.scale - c.offsetX;
        const wy = c.offsetY - cy / c.scale;
        const offsetX = cx / nextScale - wx;
        const offsetY = cy / nextScale + wy;
        return { ...c, scale: nextScale, offsetX, offsetY };
      });
    }
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [scaleBounds]);

  const fitToBuilding = useCallback(() => {
    if (!activeLevel) return;
    // See the auto-fit effect above — `expandBBox` is pure, `bb`
    // must be reassigned at every call site.
    let bb = emptyBBox();
    if (floorplan && floorplan.pixelWidth > 0 && floorplan.pixelHeight > 0) {
      const corners = floorplanWorldCorners(floorplan.affine, floorplan.pixelWidth, floorplan.pixelHeight);
      bb = expandBBox(bb, corners.tl.x, corners.tl.y);
      bb = expandBBox(bb, corners.tr.x, corners.tr.y);
      bb = expandBBox(bb, corners.bl.x, corners.bl.y);
      bb = expandBBox(bb, corners.br.x, corners.br.y);
    }
    for (const n of activeLevel.nav_graphs?.flatMap((g) => g.vertices ?? []) ?? []) bb = expandBBox(bb, n.x, n.y);
    for (const p of activeLevel.places ?? []) bb = expandBBox(bb, p.x, p.y);
    for (const d of activeLevel.doors ?? []) {
      bb = expandBBox(bb, d.v1_x, d.v1_y);
      bb = expandBBox(bb, d.v2_x, d.v2_y);
    }
    for (const lift of buildingMap?.lifts ?? []) {
      if (!lift.levels?.includes(activeLevel.name)) continue;
      const halfW = (lift.width ?? 1) / 2;
      const halfD = (lift.depth ?? 1) / 2;
      bb = expandBBox(bb, lift.ref_x - halfW, lift.ref_y - halfD);
      bb = expandBBox(bb, lift.ref_x + halfW, lift.ref_y + halfD);
    }
    if (!Number.isFinite(bb.minX)) return;
    const fitted = bb;
    setCamera((c) => {
      const next = computeFitToBuilding(fitted, c.width, c.height);
      fitScaleRef.current = next.scale;
      return next;
    });
  }, [activeLevel, buildingMap, floorplan]);

  function toggleLayer(key: keyof LayerToggles) {
    setLayers((l) => ({ ...l, [key]: !l[key] }));
  }

  return (
    <div className="nx-map-canvas-wrap">
      {colourZones && site && (
        <Suspense fallback={null}>
          <MapCleanedZones
            siteId={site.id}
            levelName={activeLevelName}
            places={levelPlaces}
            days={prefs.cleanedZones.days}
            onLayer={setCleanedLayer}
          />
        </Suspense>
      )}
      {/* Floor switching now lives in the parent screen's level-picker
          dropdown (OverviewScreen.tsx), fed by
          `useBuildingMapLevels()` — `buildingMap` only ever contains
          ONE level (whichever the parent selected), since the v1
          `building_map` key is now cached per-level rather than one
          shared key per site. A tab strip built from
          `buildingMap.levels` here would only ever show a single,
          unclickable-feeling tab (the literal "stuck on one floor"
          symptom this replaced). */}
      <div className="nx-map-canvas__viewport" ref={viewportRef}>
        <canvas
          ref={canvasRef}
          className="nx-map-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onPointerLeave={(e) => onHoverDevice?.(null, e.clientX, e.clientY)}
          data-testid="building-map-canvas"
          /* THE CAMERA, OBSERVABLE. Everything this canvas draws lives
             in a 2D context, so a test can see pixels or nothing --
             there is no element whose position or size says where the
             camera is. These two attributes are the seam the touch
             suite reads to assert that a pinch zoomed and a pan did
             not, and they are the first thing to look at in a browser
             when a gesture misbehaves. Rounded, because the exact
             float is noise at this resolution. */
          data-camera-scale={camera.scale.toFixed(4)}
          data-camera-offset={`${camera.offsetX.toFixed(3)},${camera.offsetY.toFixed(3)}`}
          /* THE FEED AND THE ROBOT MARKS, OBSERVABLE, for the same
             reason: `data-clamped` drives the desaturation (CSS) and
             says whether anything on the canvas may be read as live;
             `data-robot-marks` lists each drawn robot as
             `fleet/name:look` (solid, dim or ghost), so a check can
             write down what the map claims without a screenshot. */
          data-clamped={feed.clamped ? 'true' : 'false'}
          data-robot-marks={robotMarks.map((m) => `${m.key}:${m.style.look}`).join(' ')}
          /* The charging docks drawn on this floor, by place name. */
          data-dock-marks={levelDocks.map((d) => d.name).join(' ')}
          /* The highlight, as `kind:id:state` (`mapHighlightPlan`), so a
             check can say what the map rings without a screenshot. */
          data-highlight={
            highlight && highlightPlan ? `${highlight.kind}:${highlight.id}:${highlightPlan.state}` : ''
          }
        />
      </div>

      <div className="nx-map-canvas__controls" aria-label="Layer toggles">
        <button type="button" onClick={fitToBuilding} data-testid="map-fit-btn">
          Fit
        </button>
        <button
          type="button"
          onClick={() => setCamera((c) => ({ ...c, scale: Math.min(scaleBounds().max, c.scale * 1.2) }))}
        >
          +
        </button>
        <button
          type="button"
          onClick={() => setCamera((c) => ({ ...c, scale: Math.max(scaleBounds().min, c.scale / 1.2) }))}
        >
          −
        </button>
        <span className="nx-map-canvas__divider" />
        {/* `VISIBLE_LAYER_KEYS`, not `Object.keys(layers)`: the
            `navGraph` layer has no data source in the v1 contract, so
            offering its checkbox was offering a control that could not
            do anything. Labels come from `LAYER_LABELS` — the keys are
            an implementation detail and used to reach the screen
            verbatim as "navGraph" / "trajectories". */}
        {VISIBLE_LAYER_KEYS.map((k) => (
          <label key={k} className="nx-map-canvas__layer">
            <input
              type="checkbox"
              checked={layers[k]}
              onChange={() => toggleLayer(k)}
              data-testid={`map-layer-${k}`}
            />
            <span>{LAYER_LABELS[k]}</span>
          </label>
        ))}
        {/* The key to the square badge, while one is drawn on this floor:
            the words a site audience reads, and for ops the place name
            in the tooltip as well (`dockWords`). */}
        {levelDocks.length > 0 && (
          <span
            className="nx-map-canvas__legend"
            data-testid="map-dock-legend"
            title={levelDocks.map((d) => dockWords(d, audience)).join('\n')}
          >
            <svg
              className="nx-map-canvas__legend-glyph"
              viewBox="-10 -10 20 20"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <rect x="-9" y="-9" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M1.8 -6.8 L-4 1.1 L-0.2 1.1 L-1.8 6.8 L4 -1.1 L0.2 -1.1 Z" fill="currentColor" />
            </svg>
            <span>{DOCK_LABEL}</span>
          </span>
        )}
        {/* The key to the cleaned-zone colours, while they are drawn. Not a
            control: they are switched and set in Preferences. */}
        {colourZones && <CleanedLegend days={prefs.cleanedZones.days} inline />}
      </div>
    </div>
  );
}

